import React, { useState } from 'react';
import { HumanTasterInbox } from './HumanTasterInbox';
import { EventIngestion } from './EventIngestion';
import { AHTLMetrics } from './AHTLMetrics';
import { motion } from 'framer-motion';

type TabView = 'overview' | 'ingest' | 'inbox' | 'metrics' | 'webhooks' | 'settings';

export const AHTLDashboard: React.FC = () => {
  const [currentTab, setCurrentTab] = useState<TabView>('overview');

  const tabs = [
    { id: 'overview', name: 'Overview', icon: '🏠' },
    { id: 'ingest', name: 'Event Ingest', icon: '📥' },
    { id: 'inbox', name: 'Human Taster', icon: '🍷' },
    { id: 'metrics', name: 'Metrics', icon: '📊' },
    { id: 'webhooks', name: 'Webhooks', icon: '🔗' },
    { id: 'settings', name: 'Settings', icon: '⚙️' }
  ] as const;

  const renderTabContent = () => {
    switch (currentTab) {
      case 'ingest':
        return <EventIngestion />;
      case 'inbox':
        return <HumanTasterInbox />;
      case 'metrics':
        return <AHTLMetrics />;
      case 'webhooks':
        return <WebhooksConfig />;
      case 'settings':
        return <AHTLSettings />;
      default:
        return <AHTLOverview setCurrentTab={setCurrentTab} />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-3">
              <div className="text-2xl">🔄</div>
              <h1 className="text-2xl font-bold text-gray-900">AHTL System</h1>
              <span className="text-sm text-gray-500">AI Adjuster / Human Taster Loop</span>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setCurrentTab(tab.id as TabView)}
                className={`px-4 py-3 text-sm font-medium transition-colors border-b-2 ${
                  currentTab === tab.id
                    ? 'text-blue-600 border-blue-600'
                    : 'text-gray-600 border-transparent hover:text-gray-900 hover:border-gray-300'
                }`}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.name}
              </button>
            ))}
          </div>
        </div>
      </nav>

      {/* Content */}
      <main className="flex-1">
        {renderTabContent()}
      </main>
    </div>
  );
};

const AHTLOverview: React.FC<{ setCurrentTab: (tab: TabView) => void }> = ({ setCurrentTab }) => {
  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-4">
          Welcome to the AHTL System
        </h2>
        <p className="text-lg text-gray-600">
          A multi-step automation pipeline where AI processes incoming events and humans perform quality assurance before final publication.
        </p>
      </div>

      {/* System Flow Visualization */}
      <div className="bg-white rounded-lg shadow-sm border p-8 mb-8">
        <h3 className="text-xl font-semibold text-gray-900 mb-6">System Flow</h3>
        <div className="relative">
          <div className="flex items-center justify-between">
            {[
              { name: 'Event Ingest', icon: '📥', color: 'blue' },
              { name: 'Normalizer', icon: '🔄', color: 'purple' },
              { name: 'AI Adjuster', icon: '🤖', color: 'indigo' },
              { name: 'Evaluator', icon: '📊', color: 'yellow' },
              { name: 'Human Taster', icon: '👤', color: 'orange' },
              { name: 'Publisher', icon: '🚀', color: 'green' }
            ].map((step, index) => (
              <motion.div
                key={step.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="flex flex-col items-center relative"
              >
                <div className={`w-16 h-16 bg-${step.color}-100 rounded-full flex items-center justify-center mb-2`}>
                  <span className="text-2xl">{step.icon}</span>
                </div>
                <span className="text-sm font-medium text-gray-700">{step.name}</span>
                {index < 5 && (
                  <div className="absolute top-8 left-16 w-full">
                    <svg className="w-full h-2" viewBox="0 0 100 10">
                      <path
                        d="M 0 5 L 90 5"
                        stroke="#CBD5E1"
                        strokeWidth="2"
                        fill="none"
                      />
                      <path
                        d="M 90 5 L 85 0 M 90 5 L 85 10"
                        stroke="#CBD5E1"
                        strokeWidth="2"
                        fill="none"
                      />
                    </svg>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-lg shadow-sm border p-6 hover:shadow-md transition-shadow cursor-pointer"
          onClick={() => setCurrentTab('ingest')}
        >
          <div className="flex items-center space-x-3 mb-3">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <span className="text-2xl">📥</span>
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Ingest Event</h3>
          </div>
          <p className="text-gray-600">
            Submit new events to the processing pipeline
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-lg shadow-sm border p-6 hover:shadow-md transition-shadow cursor-pointer"
          onClick={() => setCurrentTab('inbox')}
        >
          <div className="flex items-center space-x-3 mb-3">
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
              <span className="text-2xl">🍷</span>
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Review Drafts</h3>
          </div>
          <p className="text-gray-600">
            Review and approve AI-generated content
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-lg shadow-sm border p-6 hover:shadow-md transition-shadow cursor-pointer"
          onClick={() => setCurrentTab('metrics')}
        >
          <div className="flex items-center space-x-3 mb-3">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <span className="text-2xl">📊</span>
            </div>
            <h3 className="text-lg font-semibold text-gray-900">View Metrics</h3>
          </div>
          <p className="text-gray-600">
            Monitor system performance and statistics
          </p>
        </motion.div>
      </div>

      {/* Features */}
      <div className="bg-white rounded-lg shadow-sm border p-8">
        <h3 className="text-xl font-semibold text-gray-900 mb-6">Key Features</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="flex space-x-3">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <span>🤖</span>
              </div>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900">AI Processing</h4>
              <p className="text-gray-600 text-sm">
                Automatic content enhancement using OpenAI, Claude, or custom models
              </p>
            </div>
          </div>

          <div className="flex space-x-3">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <span>👤</span>
              </div>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900">Human Review</h4>
              <p className="text-gray-600 text-sm">
                Quality assurance with human-in-the-loop approval workflow
              </p>
            </div>
          </div>

          <div className="flex space-x-3">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <span>📊</span>
              </div>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900">Confidence Scoring</h4>
              <p className="text-gray-600 text-sm">
                Automatic quality and confidence scoring with configurable thresholds
              </p>
            </div>
          </div>

          <div className="flex space-x-3">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                <span>🔗</span>
              </div>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900">Webhook Integration</h4>
              <p className="text-gray-600 text-sm">
                Publish approved content to multiple endpoints via webhooks
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const WebhooksConfig: React.FC = () => {
  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Webhook Configuration</h2>
        <p className="text-gray-600 mb-6">Configure webhook endpoints for publishing approved content.</p>
        
        <div className="space-y-4">
          <div className="border rounded-lg p-4">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="font-semibold">Production Publisher</h3>
                <p className="text-sm text-gray-600">https://api.example.com/publish</p>
                <div className="flex space-x-2 mt-2">
                  <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs">Active</span>
                  <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">draft.published</span>
                </div>
              </div>
              <button className="text-gray-400 hover:text-gray-600">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                </svg>
              </button>
            </div>
          </div>

          <div className="border rounded-lg p-4">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="font-semibold">Analytics Tracker</h3>
                <p className="text-sm text-gray-600">https://analytics.example.com/track</p>
                <div className="flex space-x-2 mt-2">
                  <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs">Active</span>
                  <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">draft.approved</span>
                  <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">draft.rejected</span>
                </div>
              </div>
              <button className="text-gray-400 hover:text-gray-600">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        <button className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
          + Add Webhook
        </button>
      </div>
    </div>
  );
};

const AHTLSettings: React.FC = () => {
  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">AHTL Settings</h2>
        
        <div className="space-y-6">
          <div>
            <h3 className="font-semibold text-gray-900 mb-3">AI Configuration</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Default AI Provider</label>
                <select className="w-full px-3 py-2 border rounded-lg">
                  <option>OpenAI GPT-4</option>
                  <option>Claude 3 Opus</option>
                  <option>Custom Model</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Temperature</label>
                <input type="number" step="0.1" min="0" max="1" defaultValue="0.7" className="w-full px-3 py-2 border rounded-lg" />
              </div>
            </div>
          </div>

          <div>
            <h3 className="font-semibold text-gray-900 mb-3">Auto-Approval Thresholds</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Confidence Threshold</label>
                <input type="number" step="0.05" min="0" max="1" defaultValue="0.95" className="w-full px-3 py-2 border rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Quality Threshold</label>
                <input type="number" step="0.05" min="0" max="1" defaultValue="0.90" className="w-full px-3 py-2 border rounded-lg" />
              </div>
            </div>
          </div>

          <div>
            <h3 className="font-semibold text-gray-900 mb-3">Processing Rules</h3>
            <div className="space-y-2">
              <label className="flex items-center">
                <input type="checkbox" defaultChecked className="mr-2" />
                <span className="text-sm">Enable auto-approval for high confidence drafts</span>
              </label>
              <label className="flex items-center">
                <input type="checkbox" defaultChecked className="mr-2" />
                <span className="text-sm">Require review for edited content</span>
              </label>
              <label className="flex items-center">
                <input type="checkbox" className="mr-2" />
                <span className="text-sm">Skip AI processing for trusted sources</span>
              </label>
            </div>
          </div>

          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            Save Settings
          </button>
        </div>
      </div>
    </div>
  );
};